import { ColorTypes, PDFDocument, PDFEmbeddedPage, PDFFont, PDFImage, PDFPageDrawTextOptions, StandardFonts, drawLine, drawRectangle, rgb } from "pdf-lib";
import { AllContent, AllSegments, Field, FontVariant, Heading, HorizAlign, List, ListItem, Margin, Paragraph, PdfContext, PdfDefaults, PdfFormatDefaults, Region, Group, Section, SubReport, TOC, TOCDocumentPosition, Table, TableRow, SignatureBlock, Img, IterationProps, ReportStructure, report_def } from "./types";
import { api } from "../../api/api";
import { toPdfPoint } from "./utils";
import { toNumber } from "../utils/conversion";
import fontkit from "@pdf-lib/fontkit";
import { DataEntryCustomScriptFunctions } from "../../data-entry/runScript";
import { NamedScopeObject, ScriptEngine } from "../utils/script";
import { createPdfScriptEngine } from "./runScript";
import { formatListNumber } from "../../formatters/format";
import { hexToPdfRGB } from "../utils/colors";

async function fetchPdfFile(url: string) {
	const sourceBuffer = await fetch(url).then((res) => res.arrayBuffer())
	const doc = await PDFDocument.load(sourceBuffer);
	return doc;
}

const BaseColors = {
	white: {
		type: ColorTypes.RGB,
		red: 1, green: 1, blue: 1,
	} as any,
	red: {
		type: ColorTypes.RGB,
		red: 1, green: 0, blue: 0,
	} as any,
	errorBG: {
		type: ColorTypes.RGB,
		red: 0.96, green: 0.584, blue: 0.619,
	} as any,
	error: {
		type: ColorTypes.RGB,
		//red: 0.592, green: 0.027, blue: 0.086,
		red: 0, green: 0, blue: 0,
	} as any,

	fieldBG: {
		type: ColorTypes.RGB,
		red: 0.67, green: 0.988, blue: 0.392,
	} as any,
	field: {
		type: ColorTypes.RGB,
		//red: 0.231, green: 0.4, blue: 0.082,
		red: 0, green: 0, blue: 0,
	} as any,

};

export interface PdfComposer {
	doc: PDFDocument,
	subReports?: {
		[k: string]: report_def,
	},
	preparedImages: PreparedImages,
	context: PdfContext,

	staticTemplates: {
		[key: string]: StaticTemplate,
	},

	scriptEngine: ScriptEngine,

	addPage: (options?: {
		landscape?: boolean,
		resetPageNumber?: boolean,
	}) => void,

	header: () => void,
	footer: () => void,

	getContext: () => PdfContext,

	pushContext: (specificContext?: PdfContext) => void,
	popContext: (options?: {
		retainOwnPosition?: boolean,
	}) => void,

	drawText: (s: string, options?: PDFPageDrawTextOptions) => void,
	getFontVariant: () => PDFFont,
	heightOfLine: () => number,

	compose: (def: report_def) => void,
	preFetchImages: (sections: Section[]) => void,

	newLine: (autoNewPage: boolean) => void,
	pageBreak: () => void,
	ensurePageExists: () => void,

	content: (items: AllContent[]) => void,
	p: (s: string) => void,
	paragraph: (para: Paragraph) => void,
	group: (group: Group) => void,
	region: (region: Region) => void,
	toc: (toc: TOC) => void,
	finalizeTOC: () => void,
	table: (tbl: Table) => void,
	hr: () => void,
	section: (sect: Section) => void,
	heading: (h: Heading) => void,
	list: (list: List) => void,
	image: (img: Img) => void,

	save: () => Promise<Uint8Array>,
}

export interface PreparedImages {
	[k: string]: {
		img: PDFImage,
		width: number,
		height: number,

		isSVG?: boolean,
	},
}

interface StaticTemplate {
	doc: PDFDocument,
	embeddedPages: PDFEmbeddedPage[],
}

export async function Pdf(props: {
	embeddedFontNames?: string[],
	defaults?: PdfFormatDefaults,
	
	scriptScope: any,

	templateBlob?: string,
	subReports?: {
		[k: string]: report_def,
	},

	showFields?: boolean,
}) {

	let doc: PDFDocument;
	let docTemplate: PDFDocument;

	const subReports = props.subReports;

	let toOverflow: AllSegments[] = [];

	// need to load all of the static docs which will be referenced during this report generation
	const staticTemplates: {
		[key: string]: StaticTemplate,
	} = {};

	doc = await PDFDocument.create();

	async function loadTemplate(blobID: string) {
		const template = await fetchPdfFile(api.getDownloadSystemUrl(blobID));

		const templatePages = template.getPages();
		const all = [];
		for(let i=0;i<template.getPageCount();i++) {
			all.push(i);
		}

		const copiedPages = await doc.copyPages(template, all);
		const embeddedPages = await doc.embedPages(copiedPages);

		return {
			doc: template,
			embeddedPages,
		}
	}

	// is there a pdf doc which will be the bg (i.e. template)
	if (props.templateBlob) {

		staticTemplates[""] = await loadTemplate(props.templateBlob);
		//await addTemplatePages(staticTemplates[""]);
	}
	for(let n in props.subReports) {
		const sr = props.subReports[n];
		if (sr.data.template && sr.data.template.blob) {
			staticTemplates[sr.id] = await loadTemplate(sr.data.template.blob);
		}
	}

	const preparedImages: PreparedImages = {};

	const defaults = props.defaults ? props.defaults : PdfDefaults;

	let isFontKitRegistered = false;

	const fonts: { [k: string]: FontVariant } = {};
	let haveAnyFont = false;
	if (props.embeddedFontNames) {
		for(let n of props.embeddedFontNames) {
			if (!defaults.fontName) defaults.fontName = n;

			if (n === "helvetica") {
				fonts.helvetica = {
					regular: await doc.embedFont(StandardFonts.Helvetica),
					bold: await doc.embedFont(StandardFonts.HelveticaBold),
					italic: await doc.embedFont(StandardFonts.HelveticaOblique),
					boldItalic: await doc.embedFont(StandardFonts.HelveticaBoldOblique),
				};
				haveAnyFont = true;

			} else if (n === "times") {
				fonts.times = {
					regular: await doc.embedFont(StandardFonts.TimesRoman),
					bold: await doc.embedFont(StandardFonts.TimesRomanBold),
					italic: await doc.embedFont(StandardFonts.TimesRomanItalic),
					boldItalic: await doc.embedFont(StandardFonts.TimesRomanBoldItalic),
				};
				haveAnyFont = true;

			} else {
				if (!isFontKitRegistered) {
					isFontKitRegistered = true;

					doc.registerFontkit(fontkit);
				}

				const font = await loadFont(n);
				fonts[n] = font;
				haveAnyFont = true;

			}
		}
	}

	if (!haveAnyFont) {
		fonts.helvetica = {
			regular: await doc.embedFont(StandardFonts.Helvetica),
			bold: await doc.embedFont(StandardFonts.HelveticaBold),
			italic: await doc.embedFont(StandardFonts.HelveticaOblique),
			boldItalic: await doc.embedFont(StandardFonts.HelveticaBoldOblique),
		};
	}

	// load fonts
	async function loadFont(name: string) {
		const result: FontVariant = {} as any;
		const variants = [
			"regular", "italic", "bold", 'boldItalic'
		]

		for(let v of variants) {
			const url = `/font/${name}-${v}.ttf`
			const response = await fetch(url);
            const fontBytes = new Uint8Array(await response.arrayBuffer());
			(result as any)[v] = await doc.embedFont(fontBytes, {
				subset: true,
			});
		}

		return result;
	}

	const contextStack: PdfContext[] = [];
	
	// initial context

	// make sure the default font size is applying to heading 0
	const defaultFontSize = defaults.fontSize ? toNumber(defaults.fontSize, 10) : 10;
	defaults.headings[0].fontSize = defaultFontSize;


	let context: PdfContext = {
		counters: {},

		measureStack: [],

		font: fonts[defaults.fontName],
		fontSize: defaultFontSize,
		textHeight: fonts[defaults.fontName].regular.heightAtSize(defaultFontSize),
		lineHeight: (defaults.lineHeight > 0) ? defaults.lineHeight : undefined,

		baselineAdjust: defaults.baselineAdjust ? defaults.baselineAdjust : 0,

		headings: defaults.headings,

		tocItems: [],
		
		page: undefined as any,
		pageWidth: 0,
		pageHeight: 0,
		landscape: false,
		pageNumber: 0,

		color: rgb(0,0,0),

		leftEdge: 0,
		rightEdge: 0,
		pageContentBottom: 0,
		pageContentTop: 0,
		x: 0,
		y: 0,

		withinHeaderFooter: false,

		retainLeftEdgeOnNewPage: false,

		yStaticOffset: 0,

		lineColor: rgb(0,0,0),
		lineThickness: 1,

		margin: {
			...defaults.margin,
		}
	};
	contextStack.push(context);

	const tocToRender = {} as TOCDocumentPosition;

	const dataWindows = [
	] as NamedScopeObject[];

	if (props.scriptScope.data) {
		dataWindows.push(
			{
				name: "data",
				obj: props.scriptScope.data,
			},
		);

		if (props.scriptScope.data.data) {
			dataWindows.push(
				{
					name: "form",
					obj: props.scriptScope.data.data["@"],
				},
			);
		}
	}



	// async function addTemplatePages(template: StaticTemplate) {
	// 	for(let p of template.embeddedPages) {
	// 		const newPage = doc.addPage();
	// 		newPage.drawPage(p);
	// 		// doc.addPage(p);
	// 	}
	// }

	function addPage(options?: {
		landscape?: boolean,
		resetPageNumber?: boolean,
		useExistingNewPage?: boolean,
	}) {
		//debugger;
		
		const landscape = (options && options.landscape) ? true : false;
		const w = landscape ? defaults.pageSize.height : defaults.pageSize.width;
		const h = landscape ? defaults.pageSize.width : defaults.pageSize.height;

		function indexOfThisPage() {
			const allPages = doc.getPages();
			let index = allPages.length;
			for(let i=0;i<allPages.length;i++) {
				if (allPages[i] === context.page) {
					return i+1;
				}
			}
			return -1;
		}

		let page;
		if (options && options.useExistingNewPage) {
			const index = indexOfThisPage();
			page = doc.getPages()[index+1];

		} else {
			if (context.addNewPagesAtThisPoint) {
				// insert after current page
				const index = indexOfThisPage();
				page = doc.insertPage(index, [w, h]);
	
			} else {
				// add to end
				page = doc.addPage([w, h]);
	
			}
	
		}

		context.page = page;
		context.pageWidth = w;
		context.pageHeight = h;
		context.landscape = landscape;
		context.x = context.retainLeftEdgeOnNewPage ? context.leftEdge : context.margin.left;

		if (options && options.resetPageNumber) {
			context.pageNumber = 1;
		} else {
			context.pageNumber++;
		}

		context.y = h - context.margin.top;
		if (context.header && (!context.header.hideFirstPage || (context.header.hideFirstPage && context.pageNumber > 1))) {
			context.y -= context.header.height;
		}

		context.yStaticOffset = 0;

		context.pageContentTop = context.y;
		context.pageContentBottom = context.margin.bottom + (context.footer ? context.footer.height : 0);

		context.leftEdge = context.x;
		context.rightEdge = w - context.margin.right;

		if (context.pageBorder) {
			pageBorder();
		}

		header();

		footer();

		return page;
	}

	function pageBorder() {
		let lineWidth = context.pageBorder?.width;
		if (!lineWidth) return; //lineWidth = 1;

		const x = context.leftEdge - lineWidth;
		const w = context.rightEdge - context.leftEdge + (lineWidth * 2);
		context.page.drawRectangle({
			borderWidth: lineWidth, //context.pageBorder.width,
			
			color: undefined,
			borderColor: rgb(0,0,0),

			x, 
			y: context.pageContentBottom - lineWidth,
			width: w,
			height: context.pageContentTop - context.pageContentBottom + (lineWidth*2),
		});

	}

	function header() {
		if (!context.header) return;

		if (context.header.hideFirstPage && context.pageNumber === 1) return;

		pushContext();

		// back to normal font
		applyHeadingStyle(0);
		context.x = context.margin.left;
		context.leftEdge = context.x;
		context.withinHeaderFooter = true;

		context.y += context.header.height;
		content(context.header.content);

		popContext({ retainOwnPosition: true });
	}

	function footer() {
		if (!context.footer) return;

		pushContext();

		// back to normal font
		applyHeadingStyle(0);
		context.x = context.margin.left;
		context.leftEdge = context.x;
		context.withinHeaderFooter = true;

		context.disableAutoNewPage = true;
		context.y = context.margin.bottom + context.footer.height;

		// line(context.x, context.y, context.x, context.y - context.footer.height);
		// line(context.rightEdge, context.y - context.footer.height, context.rightEdge, 0);


		content(context.footer.content);

		popContext({ retainOwnPosition: true });
	}

	function getContext() {
		return context;
	}

	function pushContext(specificContext?: PdfContext) {
		if (specificContext) {
			contextStack.push(specificContext);
			context = specificContext;

		} else {

			// if this is a brand new context that doesn't even have page yet then keep as-is
			if (context.page === undefined) {
				// don't add
			} else {
				const newContext = {
					...context,
				};
				contextStack.push(newContext);	
				context = newContext;	
			}
		}
	}
	function popContext(options?: {
		retainOwnPosition?: boolean,
	}) {
		// NOTE: need this check because pushContext updated to only push once have a context with valid page object
		if (contextStack.length > 1) {

			const prev = contextStack.pop();
			context = contextStack[contextStack.length-1];
	
			// transfer the current position
			if (prev) {
				context.page = prev.page;
				context.pageHeight = prev.pageHeight;
				context.pageWidth = prev.pageWidth;
				context.landscape = prev.landscape;
				context.pageNumber = prev.pageNumber;
	
				if (!options || !options.retainOwnPosition) {
					context.x = prev.x;
					context.y = prev.y;	
				}

				// also bubble back up the flag indicating a new page was required
				if (prev.newPageRequired) context.newPageRequired = prev.newPageRequired;

			}
		}
	}

	function drawText(s: string, options?: PDFPageDrawTextOptions) {
		if (context.measureStack.length > 0) return;

		const c = context;
		if (!options) options = {};

		c.page.drawText(s, {
			x: c.x,
			y: c.y - c.textHeight + (c.baselineAdjust * c.fontSize),
			size: c.fontSize,
			font: getFontVariant(),
			color: c.color,

			lineHeight: heightOfLine(),

			...options,
		});

	}

	function getFontVariant() {
		if (context.bold && context.italic) {
			return context.font.boldItalic;
		} else if (context.bold) {
			return context.font.bold;
		} else if (context.italic) {
			return context.font.italic;
		} else {
			return context.font.regular;
		}
	}

	function heightOfLine() {
		return context.textHeight * (context.lineHeight ? context.lineHeight : 1);
	}

	// return true if new page created
	function newLine(autoNewPage: boolean, useExistingNewPage?: boolean) {
		context.x = context.leftEdge;
		const lineHeight = heightOfLine();
		context.y -= lineHeight;

		if (autoNewPage && ((context.y-lineHeight) < context.pageContentBottom) && !context.disableAutoNewPage) {
			context.newPageRequired = true;

			if (context.measureStack.length === 0) {
				addPage({
					useExistingNewPage,
				});
				return true;
			}	
		}

		return false;
	}

	function p(s: string) {
		drawText(s, {
			maxWidth: context.pageWidth - context.margin.right - context.x,
		});
	}

	interface ParaResult {
		text: string,
		xFinal: number,
		yFinal: number,
		autoCreatedNewPage: boolean,
	}

	function paragraph(para: Paragraph, lastParaResult?: ParaResult) {

		// para segments can be modified in realtime when necessary
		// so this creates problems if in a repeating section etc - can't reuse the same object
		// so to be safe make a complete copy
		para = JSON.parse(JSON.stringify(para));


		// NOTE: returns the concat of all text as single string without formatting or anything
		// this is required for headings to get the text used on TOC
		let result: ParaResult = {
			text: "",
			xFinal: 0,
			yFinal: 0,
			autoCreatedNewPage: false,
		};

		pushContext();

		// are we continuing from last para?
		let useExistingNewPage = false;
		if (para.continue) {
			if (lastParaResult) {
				context.x = lastParaResult.xFinal;
				context.y = lastParaResult.yFinal;

				useExistingNewPage = lastParaResult.autoCreatedNewPage;

				// did the last para end with a space or does this start with one?
				const firstSeg = (para.segments && para.segments.length > 0) ? para.segments[0] : undefined;
				if (
					firstSeg && (
						!lastParaResult.text.endsWith(" ") 
						|| (firstSeg && firstSeg.text!.length > 0 && !firstSeg.text?.startsWith(" "))
					)
					// NOTE, don't add space if this seg starts with a comma
					&& (!firstSeg.text?.startsWith(","))
				) {
					firstSeg.text = " " + firstSeg.text;
				}
			}
		}

		if (para.fontSize) {
			context.fontSize = toNumber(para.fontSize, (defaults.fontSize > 0) ? defaults.fontSize : 8);
			context.textHeight = context.font.regular.heightAtSize(context.fontSize);
		}

		// // TODO: remove this
		// if ((para.align === HorizAlign.left || para.align === undefined)) para.align = HorizAlign.justified;

		const leftAligned = (para.align === HorizAlign.left || para.align === undefined);

		// split all words out first so only have to do once
		for(let seg of para.segments) {
			// if there is a field, then run its script to get the dynamic value
			const f = (seg as Field);
			if (f.field) {
				seg.wasCalculated = true;

				if (f.field.script) {
					let scriptResult = runScript(f.field.script!);
					// does this need to be formatted?
					if (f.field.format || f.field.forceCase) {
						scriptResult = DataEntryCustomScriptFunctions.formatter(scriptResult, f.field.format ? f.field.format : "", f.field.forceCase);
					}

					seg.text = (scriptResult !== undefined) ? String(scriptResult) : "";

					if (props.showFields && (seg.text === "field" || seg.text === "")) seg.text = "{{field}}";

					// does the field specify any formatting?
					if (f.field.bold) {
						seg.bold = f.field.bold;
					}

					// are there any new lines from this? 
					if (seg.text.indexOf("\n") >= 0) {
						const parts = seg.text.split("\n");
						
						// keep the first part on this segment
						seg.text = parts[0];

						// now insert new paragraphs for all other parts
						for(let i=1;i<parts.length;i++) {
							para.segments.push({
								...seg,
								field: undefined,
								words: undefined,
								text: parts[i],
								newLine: true,
							});
						}
					}
				} else {
					if (props.showFields) seg.text = "{{field}}";
				}
			}

			if (seg.text) {
				// NOTE: need to cater for char(160) .. nbsp
				seg.words = seg.text.replace(/\u00A0/g, " ").split(" ");

				result.text += seg.text;
			} else {
				seg.words = [];
			}
		}

		// NOTE: check if there was anything overflowing from an .overflow para that came prior to this
		if (toOverflow.length > 0) {
			// yes, so include those leftovers at the start of the list
			para.segments.splice(0, 0, ...toOverflow);
			toOverflow = [];
		}


		let position = {
			segmentIndex: 0,
			startWordIndex: 0,
		}

		function processSegments(sizingForAlignment: boolean) {
			let wordsProcessed = 0;

			let initialPosition = { ...position };

			let encounteredSegmentRequiringNewLine = false;

			context.lineSpaceCount = 0;

			// when doing justified content there are small errors which can build up, so have some allowance for it
			const tolerance = (context.spaceAdjust !== 0) ? 1 : 0;

			for(let iSeg = position.segmentIndex; iSeg < para.segments.length; iSeg++) {
				const seg = para.segments[iSeg];
				position.segmentIndex = iSeg;

				// if we have moved beyond the starting segment and hit a segment that requires a new line
				// then stop processing here - want to start the new line fresh
				if (iSeg > initialPosition.segmentIndex && seg.newLine) {
					encounteredSegmentRequiringNewLine = true;
					const x = context.x;
					newLine(true);
					if (context.measureStack.length > 0) {
						context.x = x;
					}
					break;
				}

				context.bold = seg.bold ? seg.bold : para.bold;
				context.italic = seg.italic ? seg.italic : para.italic;
	
				const font = getFontVariant();
				const fontSize = context.fontSize;
				let spaceWidth = font.widthOfTextAtSize(" ", fontSize);
				if (context.spaceAdjust) spaceWidth += context.spaceAdjust;
				context.spaceWidth = spaceWidth;
		
				for(let i = position.startWordIndex; i < seg.words!.length; i++) {
					let word = seg.words![i];

					if (para.forceCase === "upper") {
						word = word.toUpperCase();
					}

					// ignore whitespace at the start of new lines
					if (
						context.x === context.leftEdge &&
						word.trimStart() !== word
					) {
						word = word.trimStart();
					}

					let w = 
						//word ? 
						font.widthOfTextAtSize(word, fontSize)
						//: spaceWidth
						;

					// NOTE: have to cater for when a space too small for even a single word is provided
					// in this case still have to output the word otherwise it will get stuck in endless loop
					const textRightEdge = (context.x + w);

					if (
						(wordsProcessed > 0)
						&& (textRightEdge > context.rightEdge && ((textRightEdge - context.rightEdge) > tolerance))
					) {
						// if checking width for alignment calcs exit out here
						if (sizingForAlignment) {
							// adjust for last space
							context.x -= spaceWidth;
							//if (w > 0)
								context.lineSpaceCount--;
							return;
						}

						// wrap to next line
						newLine(true);

						// NOTE: only rendering line at a time to cater for alignment support
						// update the position of where we are at
						// NOTE: because this current word caused the wrap, we start here next iteration
						position.segmentIndex = iSeg;
						position.startWordIndex = i;

						return;
					}

					// don't include trailing space at end of each block
					const hasSpace = (i < seg.words!.length-1);

					// special case for hollow boxes with borders
					// i.e. for having a space to tick/cross on forms
					if (word.indexOf("□") >= 0) {
						// split it into parts
						const parts = word.split("□");
						const lineHeight = heightOfLine();
						for(let i=0;i<parts.length;i++) {
							if (i > 0) {
								pushContext();
								context.color = BaseColors.white;
								rect(context.x, context.y, lineHeight, -lineHeight);
								context.x += lineHeight;
								popContext();
							}

							drawText(parts[i]);
							context.x += font.widthOfTextAtSize(parts[i], fontSize);
						}
					
					} else if (word === "{{field}}") {
						pushContext();

						if (props.showFields) {
							context.color = BaseColors.errorBG;
							context.lineColor = BaseColors.error;
							context.lineThickness = 0;
							const pad = 2;
							rect(context.x - pad, context.y + pad, w + pad*2, -heightOfLine() - pad*2, 0.5);
							context.color = BaseColors.error;
						}
						drawText(word);
						popContext();
						context.x += w;

					} else if (seg.wasCalculated) {
						pushContext();

						if (props.showFields) {
							context.color = BaseColors.fieldBG;
							context.lineThickness = 0;
							const pad = 2;
							rect(context.x - pad, context.y + pad, w + pad*2, -heightOfLine() - pad*2, 0.5);
							context.color = BaseColors.field;
						}
						drawText(word);
						popContext();
						context.x += w;

					} else {
						// normal word render
						drawText(word); // + (hasSpace ? " " : ""));
						context.x += w;
					}
					
					if (hasSpace) {
						context.x += spaceWidth;

						// only count spaces that are after words
						//if (w > 0) 
							context.lineSpaceCount++;
					}

					wordsProcessed++;
				}

				position.startWordIndex = 0;

			}

			// at the end
			if (!sizingForAlignment) {
				// actually rendering
				if (encounteredSegmentRequiringNewLine) {
					// didn't get fully to end, just at a segment that is forcing a new line
				} else {
					// got to end
					position.segmentIndex = para.segments.length;
					position.startWordIndex = para.segments[para.segments.length-1].words!.length;		
				}
			} else {
				// just measuring
				position = initialPosition;
			}

		}

		if (para.segments.length > 0) {
			// if we have alignment other than left have to first calc exact width of line
			while (
				(position.segmentIndex < (para.segments.length - 1))
				|| (position.startWordIndex < (para.segments[para.segments.length-1].words!.length))
			) {
				if (!leftAligned || context.regionSize) {
					// work out width of next line
					context.spaceAdjust = 0;
					context.lineSpaceCount = 0;

					pushContext();
					
					context.measureStack.push("segment");

					const initialPosition = {...position};
					//debugger;
					processSegments(true);
					position = {...initialPosition};
					context.measureStack.pop();

					const spaceWidth = context.spaceWidth!;

					const w = context.x - context.leftEdge;
					const hLine = (context.regionSize) ? heightOfLine() : 0;
					const lineSpaceCount = context.lineSpaceCount;
					popContext({ retainOwnPosition: true });

					// adjust for alignment
					if (para.align === HorizAlign.center) {
						context.x = context.leftEdge + (context.rightEdge - context.leftEdge - w) / 2;

					} else if (para.align === HorizAlign.right) {
						context.x = (context.rightEdge - w - 1);

					} else if (para.align === HorizAlign.justified) {
						// only stretch the line if its near the end
						//if (w > ((context.rightEdge - context.leftEdge)*0.8)) {
						if (
							(
								lineSpaceCount! > 5
								&& (context.rightEdge - context.leftEdge - w) <= (spaceWidth * 25)
							// ) || (
							// 	lineSpaceCount! <= 5 
							// 	&& (w > ((context.rightEdge - context.leftEdge)*0.8))
							)
						) {
							context.spaceAdjust = (context.rightEdge - context.leftEdge - w) / (lineSpaceCount!);
							// line(context.x + w, context.y, context.rightEdge, context.y)
						}

						// // show count of spaces detected on line
						// let tmp = context.x;
						// let tmpCol = context.color;
						// context.x = context.rightEdge;
						// context.color = rgb(1,0,0);
						// drawText(String(lineSpaceCount) + ": " + (w + (context.spaceAdjust * lineSpaceCount!)));
						// context.color = tmpCol;
						// context.x = tmp;

					}

					if (context.regionSize) {
						const yNew = context.y - (context.regionSize.h - hLine)/2;
						context.y = yNew;
					}

				}

				// actual render
				// NOTE: nothing will get rendered if in nested measure from parent(s)
				processSegments(false);

				// is this paragraph to overflow into the next one?
				if (
					para.overflow &&

					(
						(position.segmentIndex < (para.segments.length - 1))
						|| (position.startWordIndex < (para.segments[para.segments.length-1].words!.length))
					)
				) {
					// move all remaining segments into the overflow buffer
					// for the current segment just want words left on it
					const firstSeg = para.segments[position.segmentIndex];
					// cut out words prior to where up to
					firstSeg.words?.splice(0, position.startWordIndex);
					toOverflow.push(firstSeg);

					// now add all remaining segs beyond this current one
					for(let tmp = (position.segmentIndex+1); tmp < para.segments.length; tmp++) {
						toOverflow.push(para.segments[tmp]);
					}

					break;
				}


			}
		}

		result.xFinal = context.x;
		result.yFinal = context.y;

		// move to new line		
		result.autoCreatedNewPage = newLine(true, useExistingNewPage);

		popContext();

		return result;
	}

	function group(group: Group) {

		if (group.list) {
			iterateOverList(group, process);
		} else {
			process();
		}

		function render() {
			// start margin
			applyMarginStart(group.margin);
			context.retainLeftEdgeOnNewPage = true;

			content(group.content);

			// end margin
			applyMarginEnd(group.margin);
		}

		function process() {

			if (group.keepTogether) {
				let newPage = false;

				// measure it first and see if fits on page
				pushContext();
				context.measureStack.push("group");

				render();

				newPage = context.y <  context.pageContentBottom;

				context.measureStack.pop();

				if (context.measureStack.length === 0) {
					popContext({ retainOwnPosition: true });
					
					if (newPage) addPage();

					pushContext();
					render();
					popContext();
				} else {

					popContext({ retainOwnPosition: false });
				}

			} else {
				pushContext();
				render();
				popContext();
			}
			
		}

	}

	function region(region: Region) {

		pushContext();

		const xPad = 5;

		// position at the place in doc where need to be
		const w = region.size.w - (xPad*2);
		const h = region.size.h;
		const x = region.position.x + xPad;
		const y = (context.yStaticOffset > 0 ? context.yStaticOffset : context.pageHeight) - region.position.y;

		context.leftEdge = x;
		context.rightEdge = x + w;
		context.y = y;
		context.x = x;

		// if its close to about 1 line of text, but give a bit of tolerance
		if (h <= (3 * heightOfLine())) {
			context.regionSize = {
				w, h,
			};
		}

		content(region.content);

		popContext();
	}

	function toc(toc: TOC) {
		// NOTE: there is nothing to do at the place where the TOC is first referenced
		// ... it has to be finalised at the end of the doc
		// take note that it is required, but thats it here
		if (!context.page) addPage();

		tocToRender.page = context.page;
		tocToRender.toc = toc;
		tocToRender.context = { ...context };
	}

	function finalizeTOC() {
		// this is where the actual render happens
		if (!tocToRender.page || !tocToRender.toc) return;

		// TODO: prob move these out to be configurable at some point
		const gapBetweenDotsAndText = 3;

		// restore the context at the time of the TOC
		pushContext(tocToRender.context);

		// restore the context from when the toc was first referenced
		// const toc = tocToRender.toc;
		
		const levelFormats = [
			{
				marginLeft: 0,
				listNumberIndent: toPdfPoint(15),
			},
			{
				fontSize: 14,
				bold: true,
				uppercase: true,
				marginLeft: 0,
				marginTop: toPdfPoint(4),
				listNumberIndent: toPdfPoint(15),
			},
			{
				marginLeft: 0,
				marginTop: toPdfPoint(2),
				listNumberIndent: toPdfPoint(15),
			},
			{
				marginLeft: toPdfPoint(15),
				listNumberIndent: toPdfPoint(15),
			},
			{
				italic: true,
				marginLeft: toPdfPoint(30),
				listNumberIndent: toPdfPoint(15),
			},
		];

		let lastListNumber = "";

		// now draw it
		for(let item of context.tocItems) {
			pushContext();

			let text = item.text;

			let format;
			if (item.level > 0 && item.level < levelFormats.length) {
				format = levelFormats[item.level];

				// if (format.bold) context.bold = true;
				// if (format.italic) context.italic = true;
				if (format.fontSize) context.fontSize = format.fontSize;
				if (format.uppercase) text = text.toUpperCase();

				if (format.marginTop! > 0) context.y -= format.marginTop!;
			} else {
				format = levelFormats[0];
			}

			context.x = context.leftEdge + format.marginLeft;
			const y = context.y;

			if (item.listNumber && item.listNumber !== lastListNumber) {
				drawText(item.listNumber);
				context.leftEdge += format.listNumberIndent;
				context.x = context.leftEdge + format.marginLeft;
				
				lastListNumber = item.listNumber;
			}

			context.disableAutoNewPage = true;
			const renderResult = paragraph({
				bold: format.bold,
				italic: format.italic,

				segments: [{
					text,
				}]
			});

			// page number
			const pageNumberStr = String(item.pageNumber);
			const font = getFontVariant();
			const wNumber = font.widthOfTextAtSize(pageNumberStr, context.fontSize);
			const xStart = renderResult.xFinal;
			const xEnd = context.rightEdge - wNumber;
			context.x = xEnd;
			context.y = y;
			drawText(pageNumberStr);

			const yLine = y - heightOfLine();
			dottedLine(xStart + gapBetweenDotsAndText, yLine, xEnd - gapBetweenDotsAndText, yLine);

			newLine(true);

			// is this page full?
			if ((context.y - heightOfLine()) <= context.pageContentBottom) {
				// want any new pages inserted here not at end of doc
				context.addNewPagesAtThisPoint = true;
				addPage();
			}
						
			popContext();
		}
		
		popContext();
	}

	function pageBreak() {
		addPage();
	}

	function applyMarginStart(m?: Margin) {
		if (!m) return;

		if (m.top > 0) context.y -= m.top;
		if (m.left > 0) context.leftEdge += m.left;
		context.x = context.leftEdge;
		if (m.right > 0) context.rightEdge -= m.right;
	}
	function applyMarginEnd(m?: Margin) {
		if (!m) return;
		if (m.bottom > 0) context.y -= m.bottom;
	}

	function ensurePageExists() {
		// is there a page yet?
		if (!context.page) {
			addPage();
		}
	}

	function table(tbl: Table) {
		ensurePageExists();

		function render(onlyMeasure: boolean) {
			let fitsOnPage = true;

			pushContext();

			let tblCellPadding = {
				...defaults.cellPadding
			};
			if (!tblCellPadding) tblCellPadding = {} as any;
			if (tbl.cellPadding) {
				if (tbl.cellPadding.top !== undefined) tblCellPadding.top = tbl.cellPadding.top;
				if (tbl.cellPadding.left !== undefined) tblCellPadding.left = tbl.cellPadding.left;
				if (tbl.cellPadding.right !== undefined) tblCellPadding.right = tbl.cellPadding.right;
				if (tbl.cellPadding.bottom !== undefined) tblCellPadding.bottom = tbl.cellPadding.bottom;
			}

			applyMarginStart(tbl.margin);

			// work out the column widths upfront
			const colWidths: number[] = [];
			const maxW = context.rightEdge - context.leftEdge;
			let sum = 0;
			for(let c of tbl.cols) {
				const w = c.widthPercent / 100 * maxW;
				colWidths.push(
					w,
				);
				sum += w;
			}
			// NOTE: check for under or over 100% sum of column widths and scale up/down appropriately so table is always filling available width exactly
			if (Math.abs(sum - maxW) >= 1) {
				const diffPerCol = (maxW - sum) / tbl.cols.length;
				for(let i=0;i<colWidths.length;i++) {
					colWidths[i] += diffPerCol;
				}
			}


			function processRow(r: TableRow, precalculatedRowHeight: number) {
				pushContext();
	
				// const rowLeft = context.leftEdge;
				const rowTop = context.y;

				let yFurthestDownPage = context.pageHeight;

				let topLine;
				if (!tbl.hideBorders && context.measureStack.length === 0) {
					topLine = {
						left: context.leftEdge, 
						y: context.y, 
						right: context.rightEdge,
					};
				}

				// cannot allow new pages in a table row
				// otherwise each cell can add new pages
				context.disableAutoNewPage = true;

				for(let i=0;i<r.cells.length;i++) {
					// const col = tbl.cols[i];
					const cell = r.cells[i];

					const xCellLeft = context.leftEdge;
					const xCellWidth = colWidths[i];

					let padding = {
						top: tblCellPadding.top ? tblCellPadding.top : 0,
						left: tblCellPadding.left ? tblCellPadding.left : 0,
						right: tblCellPadding.right ? tblCellPadding.right : 0,
						bottom: tblCellPadding.bottom ? tblCellPadding.bottom : 0,
					};
					if (cell.cellPadding) {
						if (cell.cellPadding.top !== undefined) padding.top = cell.cellPadding.top;
						if (cell.cellPadding.left !== undefined) padding.left = cell.cellPadding.left;
						if (cell.cellPadding.right !== undefined) padding.right = cell.cellPadding.right;
						if (cell.cellPadding.bottom !== undefined) padding.bottom = cell.cellPadding.bottom;
					}
					
					context.rightEdge = context.leftEdge + xCellWidth - (padding.right ? padding.right : 0);
					if (padding.left) context.leftEdge += padding.left;
					context.x = context.leftEdge;

					context.y = rowTop - (padding.top ? padding.top : 0);

					if (cell.bgColor && precalculatedRowHeight > 0) {
						const bgCol = hexToPdfRGB(cell.bgColor);
						pushContext();
						context.color = bgCol;
						context.lineThickness = 0;
						rect(xCellLeft, rowTop, xCellWidth, -precalculatedRowHeight);
						popContext();
					}

					let prevColor;
					if (cell.color) {
						prevColor = context.color;
						context.color = hexToPdfRGB(cell.color);
					}

					content(cell.content);

					if (prevColor) context.color = prevColor;

					context.leftEdge += -(padding.left ? padding.left : 0) + colWidths[i];

					if (padding.bottom) context.y -= padding.bottom;

					if (context.y < yFurthestDownPage) yFurthestDownPage = context.y;
				}

				popContext();

				if (topLine) {
					horizLine(topLine.left, topLine.y, topLine.right);
				}

				// move down as far as the biggest cell
				context.y = yFurthestDownPage;

				if (context.measureStack.length === 0) {
					let xLine = context.leftEdge;
					for(let i=0;i<tbl.cols.length;i++) {
						if (!tbl.hideBorders) vertLine(xLine, rowTop, context.y);
						xLine += colWidths[i];
					}
					if (!tbl.hideBorders) vertLine(xLine, rowTop, context.y);	
				}
			}

			function rowLogic(r: TableRow, index: number) {

				pushContext();
				
				context.newPageRequired = false;

				const yStart = context.y;
				context.measureStack.push("row");
				processRow(r, 0);
				const rowHeight = yStart - context.y;
				context.measureStack.pop();

				const forceToNewPage = context.newPageRequired;

				if (context.measureStack.length > 0) {
					// measuring somewhere up the chain
					popContext();

				} else {
					popContext({ retainOwnPosition: true });
					if (forceToNewPage) {
						// row doesn't fit on the page
						//debugger;
						if (!tbl.hideBorders && index > 0) horizLine(context.leftEdge, context.y, context.rightEdge);
						addPage();
					}

					// actual render
					// NOTE: either single render (normal) or keep repeating until get to bottom of page
					let repeatCount = 0;

					while (repeatCount < 100) { // want some limit just in case
						// now do actual render
						processRow(r, rowHeight);

						//if (r.repeatToPageBottom) debugger;

						if (r.repeatToPageBottom) {
							// we there yet?
							if ((context.y - rowHeight) <= context.pageContentBottom) {
								break;
							}
						} else {
							break; // only one row
						}

						repeatCount++;
					}
	
				}
			}

			function processGroup(g: Group, i: number) {
				// only care about children which are rows
				for(let c of g.content) {
					if (c.type === "tableRow") {
						rowLogic(c as TableRow, i);
					}
				}
			}

			let i = 0;
			for(let r of tbl.rows) {

				if (r.visibility) {
					const scriptResult = runScript(r.visibility);
					if (!scriptResult) continue;
				}

				if (r.list) {
					iterateOverList(r, () => {
						if (r.type === "group") {
							processGroup(r as any, i);
						} else {
							rowLogic(r, i);
						}
					})
				} else {
					if (r.type === "group") {
						processGroup(r as any, i);
					} else  {
						rowLogic(r, i);
					}
				}


				i++;
			}

			if (!tbl.hideBorders) horizLine(context.leftEdge, context.y, context.rightEdge);

			if (context.y < context.pageContentBottom) {
				fitsOnPage = false;
			}

			// debugger;
			applyMarginEnd(tbl.margin);

			popContext({ retainOwnPosition: onlyMeasure });

			// reset the x position back to the left edge
			context.x = context.leftEdge;

			return fitsOnPage;
		}

		// can this table break pages?
		if (tbl.allowBreak) {
			// yes, so just render direct
			render(false);

		} else {
			// no, so have to measure first
			context.measureStack.push("table");
			const fitsOnPage = render(true);
			context.measureStack.pop();

			// if (context.measureStack.length === 0) {
				if (!fitsOnPage && !context.withinHeaderFooter) {
					if (context.measureStack.length === 0) {
						addPage();
					}
				}
				render(false);	
			// }
		}

		
	}

	function horizLine(x: number, y: number, xx: number) {
		line(x, y, xx, y);
	}
	function vertLine(x: number, y: number, yy: number) {
		line(x, y, x, yy);
	}

	function content(items: AllContent[]) {
		let lastParaResult;

		for(let item of items) {
			// is there any visibility criteria for this element? 
			if (item.visibility) {
				const scriptResult = runScript(item.visibility);
				if (!scriptResult) continue;
			}

			if (item.type === "p") {
				// keep track of some key things of last paragraph processing status
				// this is important for being able to continue on previous paragraph where necessary
				lastParaResult = paragraph(item as Paragraph, lastParaResult);

			} else if (item.type === "table") {
				table(item as Table);

			} else if (item.type === "pageBreak") {
				pageBreak();

			} else if (item.type === "heading") {
				heading(item as Heading);

			} else if (item.type === "list") {
				list(item as List);

			} else if (item.type === "hr") {
				hr();

			} else if (item.type === "toc") {
				toc(item as TOC);

			} else if (item.type === "group") {
				group(item as Group);

			} else if (item.type === "region") {
				region(item as Region);

			} else if (item.type === "image") {
				image(item as Img);

			} else if (item.type === "sub-report" && subReports) {
				// get the sub report
				const srDef = item as SubReport;
				if (!srDef.report || !srDef.report.def) continue;

				const sr = subReports[srDef.report.def];
				if (!sr) continue;

				// is report static?
				let template: StaticTemplate;
				if (sr.data.layoutType === "static") {
					template = staticTemplates[sr.id];
				}
				
				// embedded sub reports
				for(let s of sr.data.sections) {
					section(s, template!);
				}

			} else if (item.type === "sig-block") {
				const sig = item as SignatureBlock;
				signatureBlock(sig);

			} else if (item.type === "pagebreak") {
				pageBreak();
				
			}
		}
	}

	function modifyParagraph(c: AllContent, modification: any) {
		if (c.type === "p") {
			for(let m in modification) {
				(c as any)[m] = modification[m];
			}
		} else if (c.type === "group") {
			const g = c as Group;
			for(let child of g.content) {
				modifyParagraph(child, modification);
			}
		}
	}

	function signatureBlock(sig: SignatureBlock) {

		const compact = sig.layout === "compact";

		let height = compact ? toPdfPoint(10) : toPdfPoint(22);
		if (sig.sigHeight) {
			height = sig.sigHeight;
		}

		let width = toPdfPoint(80);
		const maxWidth = context.rightEdge - context.x;
		if (width > maxWidth) width = maxWidth;
		const bottomGap = toPdfPoint(7);

		// fetch any text to show
		let entity;
		if (sig.entity) {
			entity = runScript(sig.entity);
			if (entity && entity.entity) entity = entity.entity; // i.e. party.entity

			// replace content with something appropriate for the entity type
			sig.content = [];

			if (entity.entityType === "company") {
				sig.content.push(
					{
						align: "center",
						segments: [
							{ text: entity.company.name },
							{ text: "ACN " + entity.company.acn, newLine: true, },
						],
						type: "p",
					},
					{
						align: "center",
						segments: [
							{ text: "Authorised Signatory", },
						],
						type: "p",
					},
				);

			} else {
				sig.content.push(
					{
						align: "center",
						segments: [
							{
								field: {
									script: sig.entity,
									format: "name",
								},
							},
						],
						type: "p",
					},
				);

			}

		}

		// check the content of the sig block
		// typically will be two lines:
		// 		NAME IN UPPER CASE
		//		Position in Title Case and slightly smaller font
		if (sig.content && !sig.retainFormatting) {
			if (sig.content.length >= 1) {
				const first = sig.content[0];
				modifyParagraph(first, {
					forceCase: "upper",
				});

				if (sig.content.length >= 2) {
					const second = sig.content[1];
					modifyParagraph(second, {
						fontSize: 9,
					});	
				}

				// if compact layout then make sure all text left aligned
				if (compact) {
					const mod = {
						align: "",
					}
					for(let c of sig.content) {
						modifyParagraph(c, mod);
					}
				}
			}
		}
		
		function render(knownTextHeight?: number) {
			context.y -= height;

			if (context.measureStack.length === 0) {
				line(context.x, context.y, context.x+width, context.y);
				// line(context.x, context.y, context.x, context.y+height);
			}

			if (!compact) {
				context.y -= bottomGap;
				const rightEdge = context.x + width;
				// only reduce right edge if possible
				if (rightEdge < context.rightEdge) context.rightEdge = rightEdge;

			} else {
				context.x += width + bottomGap;
				context.leftEdge = context.x;
				if (knownTextHeight) {
					context.y += knownTextHeight;
				} else {
					context.y += height;				
				}
			}
	
			// children content
			const yStartText = context.y;
			content(sig.content);
			const textHeight = yStartText - context.y;
			return textHeight;			
		}

		// first measure
		pushContext();
		context.newPageRequired = false;

		context.measureStack.push("signature");
		const textHeight = render();
		context.measureStack.pop();

		const isNewPageRequired = context.newPageRequired;
		popContext({retainOwnPosition: true});

		// now render
		if (context.measureStack.length === 0) {
			pushContext();
			if (isNewPageRequired) {
				addPage();
			}
			render(textHeight);
			popContext();
			context.y -= bottomGap;
			newLine(false);
			// context.x = context.leftEdge;
			// context.y += context.new
		}
		
	}

	function hr() {
		// // not sure how exactly to do this, but makes sense that a hr takes the height of a normal line
		// // so do vertical in center of space that is line height
		// const h = heightOfLine();
		// const hTotal = h/2;
		// context.y -= hTotal / 2;
		const pad = 1;
		context.y -= pad;
		horizLine(context.leftEdge, context.y, context.rightEdge);
		context.y -= pad;
		// context.y -= hTotal / 2;
	}

	function bullet(
		x: number, 
		y: number,
		size: number,
	) {
		if (context.measureStack.length > 0) return;

		context.page.drawCircle({
			x,
			y,
			size,
			color: context.lineColor,
			// borderColor: context.lineColor,
			// borderWidth: context.lineThickness,
		});
	}

	function rect(
		x: number, 
		y: number,
		width: number,
		height: number,

		opacity?: number,
	) {
		if (context.measureStack.length > 0) return;

		context.page.drawRectangle({
			x,
			y,
			width,
			height,
			color: context.color,
			borderColor: context.lineColor,
			borderWidth: context.lineThickness,
			opacity,
		});
	}

	function line(
		x: number, 
		y: number,
		xx: number,
		yy: number,
	) {
		if (context.measureStack.length > 0) return;

		context.page.drawLine({
			start: {
				x,
				y,
			},
			end: {
				x: xx,
				y: yy,
			},
			color: context.lineColor,
			thickness: context.lineThickness,
		});
	}

	function dottedLine(
		x: number, 
		y: number,
		xx: number,
		yy: number,
	) {
		if (context.measureStack.length > 0) return;

		context.page.drawLine({
			start: {
				x,
				y,
			},
			end: {
				x: xx,
				y: yy,
			},
			color: context.lineColor,
			thickness: context.lineThickness,
			dashArray: [1,1],
		});
	}

	function compose(def: report_def) {

		function process(root: ReportStructure) {
			for(let s of root.sections) {
				if (s.type === "section") {
					section(s, staticTemplates[""]);
	
				} else if (s.type === "sub-report" && subReports) {
					// get the sub report
					const srDef = (s as any) as SubReport;
					if (!srDef.report || !srDef.report.def) continue;

					const sr = subReports[srDef.report.def];
					if (!sr) continue;

					process(sr.data);
					
				}
			}	
		}

		process(def.data);
	}

	function iterateOverList(def: IterationProps, fnRunPerIteration: () => void) {
		//debugger;
		if (!def.list) return;

		// find the array we iterating over
		// if (def.list === "share.holders") {
		// 	debugger;
		// }
		let arr;
		
		// is it a hard coded repeat count as a number?
		const fixedCount = parseInt(def.list);
		if (fixedCount > 0) {
			arr = new Array(fixedCount).fill({});

		// does the def.list have any brackets in it - i.e. its likely script to execute?
		} else if (def.list.indexOf("(") >= 0) {
			arr = runScript(def.list);

		} else {
			// just assume its a name of a prop
			arr = runScript( `get("${def.list}")`);
		}
		
		if (!arr) return;

		// is this list we are iterating over an index into another list?
		let lookupWithinArr;
		if (def.lookupContainer) {
			lookupWithinArr = runScript( `get("${def.lookupContainer}")`);
			if (!lookupWithinArr) {
				debugger;
				return;
			}
		}

		const hasFilter = def.filter && def.filter.trim() !== "";

		if (def.counterName) {
			context.counters[def.counterName] = 0;
		}

		let i = 0;
		for(let sourceArrayItem of arr) {

			let arrayItem = sourceArrayItem;
			if (lookupWithinArr) {
				let found = false;
				for(let l of lookupWithinArr) {
					if (l.containerID === arrayItem) {
						arrayItem = l;
						found = true;
						break;
					}
				}

				if (!found) {
					debugger;
					continue;
				}
			}

			dataWindows.push({
				name: def.scopeName!,
				obj: arrayItem,
			});

			let renderThis = true;

			// is there any filtering to apply?
			if (hasFilter) {
				const filterResult = runScript(def.filter);
				if (!filterResult) renderThis = false;
			}
			
			if (renderThis) {
				// new page?
				if (def.newPage) {
					if (i > 0 || (i === 0 && !def.newPageSkipFirst)) {
						addPage();
					}
				} else if (def.pageBreakBefore) {
					if (
						(def.continuous && i === 0)
						|| (!def.continuous)
					) {
						addPage();
					}
				}

				fnRunPerIteration();
				
				if (def.counterName) {
					context.counters[def.counterName]++;
				}

				i++;
			}

			dataWindows.pop();
		}

	}

	function section(sect: Section, template?: StaticTemplate) {

		let iteration = 0;
		let startPageIndex = doc.getPageCount() - 1;
		if (startPageIndex < 0) startPageIndex = 0;

		if (sect.list) {
			iterateOverList(sect, process);
		} else {
			process();
		}

		function process() {

			// is there any visibility criteria on this section?
			if (sect.visibility) {
				const scriptResult = runScript(sect.visibility);
				if (!scriptResult) return;
			}

			// NOTE: if the section we are about to start is .continuous then want to adopt the header/footer from current section
			if (sect.continuous) {
				sect.header = context.header;
				sect.footer = context.footer;

			} else {
				// new section, new header/footer potentially
				// update the context to reference this new header/footer
				context.header = sect.header;
				context.footer = sect.footer;

				context.pageBorder = sect.border;

			}

			if (
				(
					(
						!sect.continuous
						|| (
							sect.continuous
							&& sect.pageBreakBefore
						)
					)
					//&& !(template) 
				)
			) {
				addPage({
					landscape: sect.landscape,
					resetPageNumber: !sect.continuous,
				});
			}

			// if we have static layout then move current focus to the correct page
			if (sect.pdfPageIndex && template) {

				const templatePage = template.embeddedPages[sect.pdfPageIndex! - 1];

				let yDraw = 0;

				// is there any page?
				if (!context.page) {
					addPage();
				}

				// where does this page start?
				let yStart = context.y;
				if (sect.fixedHeight && ((context.y - sect.fixedHeight!) >= context.pageContentBottom)) {
					//addPage();
					//yStart = context.y;
				}
				if ((yStart + context.margin.top) >= context.pageHeight) yStart = context.pageHeight;

				//yDraw = -(context.pageHeight - yStart); 
				yDraw = -(templatePage.height - yStart); 
				context.yStaticOffset = yStart;	

				// if (yDraw === undefined) {
				// 	yDraw = context.page.getHeight() - templatePage.height; // draw at top
				// }

				context.page.drawPage(templatePage, {
					y: yDraw,
				});


				// debugger;
				// const pageIndex = sect.pdfPageIndex! - 1 + startPageIndex + iteration;
				// while (doc.getPageCount() <= pageIndex) {
					//doc.addPage();
				//	addPage();
				// }
				// const page = doc.getPage(pageIndex);
				// context.page = page;

				// if this is > 0 iteration then need to add in the page from the template again
				//if (iteration > 0) {
					// const template = staticTemplates[""];
					// if (template) {
					// 	const templatePage = template.embeddedPages[sect.pdfPageIndex! - 1];

					// 	if (yDraw === undefined) {
					// 		yDraw = context.page.getHeight() - templatePage.height; // draw at top
					// 	}

					// 	context.page.drawPage(templatePage, {
					// 		y: yDraw,
					// 	});
					// }

					// templatePage.drawPage()
					// doc.addPage(templatePage);	
				//}


				context.pageHeight = context.page.getHeight();
				context.pageWidth = context.page.getWidth();
			}

			pushContext();

			content(sect.content);

			if (sect.fixedHeight! > 0) {
				popContext({
					retainOwnPosition: true,
				});
				context.y -= sect.fixedHeight!;

			} else {
				popContext();

			}

			iteration++;
		}

	}

	function applyHeadingStyle(level: number) {
		// get the style attributes for heading at this level
		const style = context.headings[level];
		context.fontSize = style.fontSize;
		if (style.bold) context.bold = true;
		if (style.italic) context.italic = true;
		if (style.color) context.color = style.color;

		if (style.margin) {
			context.y -= style.margin.top;
		}

		return style;
	}

	function heading(h: Heading) {
		pushContext()

		// if we aren't at start of line then go to new line
		if (context.x !== context.leftEdge) {
			newLine(true);
		}

		const style = applyHeadingStyle(h.level);

		const renderResult = paragraph(h);

		if (
			context.measureStack.length === 0
			&& !context.withinHeaderFooter // ignore anything in header/footers
			&& tocToRender.page // ignore anything prior to seeing ref to toc in document
		) {
			context.tocItems.push({
				level: h.level,
				text: renderResult.text,
				listNumber: context.listNumberPrefix,
				pageNumber: context.pageNumber,
			});
		}

		if (style.margin) {
			context.y -= style.margin.bottom;
		}

		popContext();
	}

	function getParentContext() {
		return contextStack[contextStack.length-2];
	}

	function list(list: List) {
		ensurePageExists();
		
		pushContext();

		context.retainLeftEdgeOnNewPage = true;

		applyMarginStart(list.margin);

		const parentContext = getParentContext();
		const parentListPrefix = parentContext.listNumberPrefix ? parentContext.listNumberPrefix : "";

		// NOTE: if ignoreFormatOneItem then we first need to count how many items there will be
		let count = 0;
		let lookingForMoreThanOneItem = false;
		let totalItemCount = 0;
		let ignoreListFormatting = false;
		if (list.ignoreFormatOneItem) {
			pushContext();
			lookingForMoreThanOneItem = true;
			context.measureStack.push("looking-for-more-than-one-item");
			processAllItems();
			context.measureStack.pop();
			ignoreListFormatting = (totalItemCount <= 1);
			lookingForMoreThanOneItem = false;
			totalItemCount = 0;
			popContext({ retainOwnPosition: true });
		}

		function processItem(item: ListItem) {
			pushContext();

			totalItemCount++;

			// let countText = String(count);
			if (!ignoreListFormatting) {
				const listNumber = formatListNumber(
					count,
					(list.style) ? list.style : defaults.listStyle,
					parentListPrefix
				);

				// check if the first content item in this list item is a heading and if so adopt its style
				pushContext();
				if (item.content && item.content.length > 0 && item.content[0].type === "heading") {
					const h = item.content[0] as Heading;
					applyHeadingStyle(h.level);
				}

				// draw own bullet dot so have better control over consistency of it
				if (listNumber === "•") {
					const h = heightOfLine();
					const sz = h/6;
					const marginTop = (list.itemMargin && list.itemMargin.top > 0) ? list.itemMargin.top : 0;
					bullet(context.x + sz + 1, context.y - h/2 - marginTop, sz);

				} else {
					// normal list number
					drawText(listNumber);
				}



				popContext();

				context.listNumberPrefix = listNumber;
			}


			pushContext();

			if (!ignoreListFormatting) {
				context.leftEdge += (list.indent !== undefined) ? list.indent : defaults.listIndent;
				context.x = context.leftEdge;
			}
			
			applyMarginStart(list.itemMargin);
			content(item.content);
			applyMarginEnd(list.itemMargin);

			popContext();

			popContext();

			context.x = context.leftEdge;
		}

		function processAllItems() {
			count = toNumber(list.start, 1);
			if (count < 1) count = 1;
			for(let item of list.items) {

				if (lookingForMoreThanOneItem && totalItemCount >= 1) {
					return;
				}

				// is there any visibility criteria for this element? 
				if (item.visibility) {
					const scriptResult = runScript(item.visibility);
					if (!scriptResult) continue;
				}
	
				// pushContext();
				
				// processItem(item, true);
				// const forceToNewPage = ((context.y < context.pageContentBottom) && !context.disableAutoNewPage);

				// popContext({ retainOwnPosition: true });

				// if (forceToNewPage) {
				// 	// doesn't fit on the page
				// 	addPage();
				// }

				if (item.list) {
					iterateOverList(item, () => {
						processItem(item);
					})
				} else {
					processItem(item);
				}			

				count++;
			}
		}

		processAllItems();

		applyMarginEnd(list.margin);

		popContext();
	}

	function image(img: Img) {
		if (!img.image || !img.image.blob) return;

		const prepID = 
			(img.image.ext === ".svg") 
			? (img.image.blob + "-" + img.width + "x" + img.height) 
			: img.image.blob;

		const imgData = preparedImages[prepID];
		if (!imgData) return;

		pushContext();

		const scale = img.scale ? img.scale : 1;

		const width = (img.width ? img.width : imgData.width) * scale / 100;
		const height = (img.height ? img.height : imgData.height) * scale / 100;
	
		let x = context.x;
		let y = context.y - height;

		applyMarginStart(img.margin)

		if (img.align === "center") {
			x = context.leftEdge + ((context.rightEdge - context.leftEdge - width) / 2);
		} else if (img.align === "right") {
			x = context.rightEdge - width;
		}

		context.page.drawImage(imgData.img, {
			x, 
			y,
			width,
			height,
		});

		context.y -= height;

		applyMarginEnd(img.margin);

		popContext();
	}

	async function preFetchImages(sections: Section[]) {
		const blobs: {
			[k: string]: {
				ext: string,
			}
		} = {};

		const svgs: Img[] = [];

		// go through all the content to find any images
		function scan(c: any) {
			if (c.type === "image") {
				const img = c as Img;
				if (img.image && img.image.blob && (!blobs[img.image.blob])) {
					if (img.image.ext === ".svg") {
						svgs.push(img);
					} else {
						blobs[img.image.blob] = img.image;
					}
				}
			}

			if (c.content) {
				for(let child of c.content) {
					scan(child);
				}

				if (c.type === "section") {
					if (c.header) {
						scan(c.header);
					}
					if (c.footer) {
						scan(c.footer);
					}
				}

			} else if (c.type === "sub-report" && subReports && c.report && c.report.def) {
				const rpt = subReports[c.report.def];
				if (rpt) {
					for(let s of rpt.data.sections) {
						scan(s);
					}
				}

			}
		}

		for(let s of sections) {
			scan(s);
		}

		async function fetchImage(url: string) {
			return new Promise((resolve, reject) => {
				fetch(url).then((response) => {
					if (!response.ok) {
						throw new Error(`Failed to fetch image: ${response.statusText}`);
					}

					response.arrayBuffer().then((buf) => {
						resolve(buf);
					})
					// const buf = await response.arrayBuffer();
					// resolve(buf);
				}).catch((e) => {
					debugger;
					reject(e);
				});
	
			});

		}

		// svgs need to be converted to raster
		for(let svgImg of svgs) {
			const blobID = svgImg.image.blob;
			const blobSpecificID = blobID + "-" + svgImg.width + "x" + svgImg.height;

			// only want to add one prepared image per width/height variation
			const prep = preparedImages[blobSpecificID];
			if (!prep) {
				let url = api.getDownloadSystemUrl(blobID);
				const imgData = await fetchImage(url) as any;
				
				const canvas = document.createElement('canvas');
				canvas.width = svgImg.width;
				canvas.height = svgImg.height;
				const ctx = canvas.getContext('2d')!;

				// Create an SVG element from the SVG string
				var blob = new Blob([imgData], { type: "image/svg+xml" });
				var urlSVG = URL.createObjectURL(blob);
				const imgSVG: any = await loadSVG(urlSVG);
				ctx.drawImage(imgSVG, 0, 0, svgImg.width, svgImg.height);

				const pngDataUrl = canvas.toDataURL('image/png');
				const pngImageBytes = await fetch(pngDataUrl).then(res => res.arrayBuffer());
				const img = await doc.embedPng(pngImageBytes);

				preparedImages[blobSpecificID] = {
					img,
					width: svgImg.width,
					height: svgImg.height,
				};
				
			}


		}

		function loadSVG(url: any) {
			return new Promise((resolve, reject) => {
				var img = new Image();
				img.onload = function () {
					resolve(img);
				};
				img.onerror = function (e) {
					reject(e);
				}
				img.src = url;
			});
		}


		// fetch each of the images - standard raster images - png/jpg
		for(let blobID in blobs) {
			const b = blobs[blobID];
			let url = api.getDownloadSystemUrl(blobID);

			const imgData = await fetchImage(url) as any;

			try {
				
				let img: any;

				const ext = b.ext.toLowerCase();
				if (ext === ".png") {
					img = await doc.embedPng(imgData);

				} else if (ext === ".jpg") {
					img = await doc.embedJpg(imgData);

				}

				if (img) {
					preparedImages[blobID] = {
						img,
						width: img.width,
						height: img.height,
						isSVG: (ext === ".svg" ? true : false),
					};
				}
	
			} catch(e) {
				debugger;
			}
			
		}

	}

	async function save() {
		finalizeTOC();

		const bytes = await doc.save();
		return bytes;
	}

	const composer: PdfComposer = {
		context,
		doc,
		preparedImages,
		subReports,
		staticTemplates,
		scriptEngine: {} as ScriptEngine,

		addPage,
		
		drawText,

		p,
		paragraph,
		pageBreak,
		section,
		content,
		table,
		list,
		header,
		footer,
		pushContext,
		popContext,
		getFontVariant,
		heightOfLine,
		newLine,
		ensurePageExists,
		group,
		region,
		toc,
		finalizeTOC,
		hr,
		heading,
		image,

		getContext,

		preFetchImages,

		compose,

		save,
	};

	const scriptEngine = createPdfScriptEngine(
		composer,
	);
	composer.scriptEngine = scriptEngine;

	function runScript(
		script: string,
	) {
		return scriptEngine.run(
			script,
			{
				...props.scriptScope,
				
				// all of the pdf context
				context,

				// some key things that are often used
				pageNumber: context.pageNumber,
				counters: context.counters,

			},
			dataWindows,
		);
	}


	return composer

}
